import {useCallback, useEffect, useRef, useState} from 'react';
import onNextFrame from '../../../../../utility/onNextFrame';

// Maria, 18 October 2026: a sub-tab's content starts directly under its tab, like a folder tab. Returns a ref for the
// content, how far it is indented to line up with the tab, and whether that has been measured yet. The content stays
// hidden until it has been measured, so it does not appear first at the left and then jump under its tab.
const useIndentUnderTab = (tabTestId, endTabTestId) => {
    const nodeRef = useRef(null);
    const [indent, setIndent] = useState(null);
    const [toLastTab, setToLastTab] = useState(0);
    const [tabEdges, setTabEdges] = useState([]);
    const [openTab, setOpenTab] = useState(null);

    const measure = useCallback(() => {
        const box = nodeRef.current;
        // the content may itself be a Paper, so look from its parent for the Paper holding the tabs
        const paper = box?.parentElement?.closest('.MuiPaper-root');
        // Maria, 19 October 2026: or a tab of the profile itself, such as Banking on a staff profile, with the row of
        // sub-tabs in the box still giving the open tab
        const tab = paper?.querySelector(`[data-testid="${tabTestId}"]`) ?? document.querySelector(`[data-testid="${tabTestId}"]`);
        const container = box?.parentElement;
        if (!tab || !container) return;
        const row = paper?.contains(tab) ? tab.closest('[role="tablist"]') : paper?.querySelector('[role="tablist"]');
        // Maria, 19 October 2026: every box starts at the left-hand edge, on student and staff profiles, with its top line
        // left open under its tab; positions are measured from the left-hand edge of the box
        const tabLeft = container.getBoundingClientRect().left;
        setIndent(0);
        // Maria, 18 October 2026: the width from this tab to the end of the last tab, so the content can end in line
        // with the last tab
        const tabs = row?.querySelectorAll('[role="tab"]');
        // the content ends in line with the tab named, or with the last tab
        // (Maria, 19 October 2026: or with a tab of the profile itself, such as Documents on a staff profile)
        const lastTab = (endTabTestId && (row?.querySelector(`[data-testid="${endTabTestId}"]`) ??
            document.querySelector(`[data-testid="${endTabTestId}"]`))) ||
            tabs?.[tabs.length - 1];
        setToLastTab(lastTab ? Math.max(0, lastTab.getBoundingClientRect().right - tabLeft) : 0);
        // Maria, 19 October 2026: on a phone or small tablet (under 900px) the content uses the full width
        if (window.innerWidth < 900) {
            setIndent(0);
            setToLastTab(0);
        }
        // the right-hand edge of this tab and each tab after it, measured from this tab
        setTabEdges([...(tabs ?? [])]
            .map(each => Math.round(each.getBoundingClientRect().right - tabLeft))
            .filter(edge => edge > 0));
        // Maria, 18 October 2026: where the open tab is, so the outline of the content can join the outline of the tab
        const selected = row?.querySelector('[role="tab"][aria-selected="true"]');
        if (selected) {
            const rect = selected.getBoundingClientRect();
            setOpenTab({start: Math.round(rect.left - tabLeft), end: Math.round(rect.right - tabLeft)});
        }
    }, [tabTestId, endTabTestId]);

    // measured as soon as the content is on the page, before it is shown
    const ref = useCallback(node => {
        nodeRef.current = node;
        if (node) measure();
    }, [measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        // Maria, 19 October 2026: the tabs can change size without the window changing (their spacing is shared out to
        // fill the row), so the content watches the tabs themselves too
        const tabs = nodeRef.current?.parentElement?.closest('.MuiPaper-root')?.querySelectorAll('[role="tab"]') ?? [];
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onNextFrame(() => measure()));
        tabs.forEach(tab => observer?.observe(tab));
        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [measure]);

    return [ref, indent ?? 0, indent !== null, toLastTab, tabEdges, openTab];
};

export default useIndentUnderTab;
